import { FC, useState } from 'react';
import Image from 'next/image';
import { Modal } from 'antd';
import cn from 'classnames';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { ButtonIwStyled } from '@/_pages/interwill/verification/_pageModule/components/styled';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { WelcomeModalProps } from '@/widgets/modals/types';

import { Onboarding } from './components/Onboarding';

const STEPS_COUNT = 7;

export const WelcomeModal: FC<WelcomeModalProps> = ({ step: savedStep }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'welcome' });
  const isDesktop = useIsDesktop();
  const modals = useModalsStore();
  const [step, setStep] = useState(savedStep ?? 0);

  const isFirstStep = step < 1;
  const isLastStep = step >= STEPS_COUNT - 1;

  const goBack = () => {
    if (isFirstStep) return;
    setStep((prevStep) => prevStep - 1);
  };

  const goNext = () => {
    if (isLastStep) {
      modals.closeModal();
      return;
    }

    setStep((prevStep) => prevStep + 1);
  };

  if (step >= 1 && step <= 5)
    return <Onboarding step={step + 1} maxSteps={STEPS_COUNT} onBack={goBack} onNext={goNext} />;

  return (
    <StyledModal
      open={true}
      centered={isDesktop}
      footer={null}
      width={800}
      height={isDesktop ? 582 : undefined}
      closable={isLastStep}
      maskClosable={false}
      onCancel={modals.closeModal}
    >
      <Banner
        src={`/assets/modals/welcome-modal/${isDesktop ? 'desktop' : 'mobile'}/step-${
          step + 1
        }.png`}
        width={isDesktop ? 800 : 358}
        height={isDesktop ? 330 : 191}
        alt={t(`step${step + 1}.title`)}
        priority
      />
      <Body>
        <Title>{t(`step${step + 1}.title`)}</Title>
        <Text>{t.rich(`step${step + 1}.text`)}</Text>
      </Body>
      <Footer className={cn({ end: !isLastStep })}>
        {isLastStep && (
          <Button
            onClick={goBack}
            style={{ visibility: isFirstStep ? 'hidden' : 'visible' }}
            type="default"
          >
            {t('btnBack')}
          </Button>
        )}
        <ProgressText>
          {step + 1}/{STEPS_COUNT}
        </ProgressText>
        {isLastStep ? (
          <ButtonIwStyled onClick={goNext}>{t('btnDone')}</ButtonIwStyled>
        ) : (
          <Button onClick={goNext}>{t('btnStart')}</Button>
        )}
      </Footer>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  &&& {
    min-height: 592px;
    --sb-size: 16px;

    & > div,
    .ant-modal-content {
      height: inherit;
      display: flex;
      flex-direction: column;
    }

    .ant-modal-body {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .ant-modal-content {
      padding: 0;
      color: ${COLORS.systemDark300};
    }

    ${media.mobile} {
      top: 0;
      margin-top: 58px;
    }
  }
`;

const Banner = styled(Image)`
  border-radius: 12px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  object-fit: cover;
  min-width: 100%;
  transition: translate 200ms ease-in-out;

  ${media.mobile} {
    height: 100%;
    flex: 1;
    width: 100%;
  }
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px;

  ${media.mobile} {
    flex: auto;
    padding: 16px;
    gap: 16px;
  }
`;

const Title = styled.h2`
  font-family: 'Manrope';
  font-weight: 600;
  font-size: 22px;
  line-height: 24px;
`;

const Text = styled.p`
  line-height: 24px;

  ${media.mobile} {
    font-size: 14px;
    line-height: 20px;
  }
`;

const Footer = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;

  ${media.iwDesktop} {
    &.end {
      justify-content: flex-end;
    }
  }
`;

const Button = styled(ButtonIw)`
  min-width: 72px;

  ${media.iwMobile} {
    font-size: 14px !important;
  }
`;

const ProgressText = styled.div`
  color: ${COLORS.systemDark200};
  font-weight: 700;

  ${media.iwDesktop} {
    position: absolute;
    left: calc(50% - 12px);
  }
`;
